import React, { Component } from "react";
import { Alert, Spinner } from "reactstrap";
import confetti from "canvas-confetti";
import { TesterOutcomeUpload } from "./TestOutcomeUpload";
import { RouteComponentProps } from "react-router";
import { ProviderService } from "../generated";
import { RequestResponseResults } from "./RequestResponseResults";
import DOMPurify from "dompurify";

export interface Props extends RouteComponentProps {}

export interface State {
  loading: boolean;
  syncing: boolean;
  authSuccess: boolean;
  syncSuccess: boolean;
  missionComplete: boolean;
  error: string;
  counter: number;
  confetti: boolean;
}

export class AccountsAuthCallback extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      syncing: false,
      authSuccess: false,
      syncSuccess: false,
      missionComplete: false,
      error: null,
      counter: -1,
      confetti: false,
    };
  }

  async componentDidMount() {
    const rawCode = new URLSearchParams(this.props.location.search).get("code");
    const sanitizedCode = DOMPurify.sanitize(rawCode);

    const data = await ProviderService.providerAuthenticateCallback({
      code: sanitizedCode,
    });

    this.setState({
      loading: false,
      authSuccess: data.success,
      syncing: data.success,
      error: data.error,
      confetti: true,
    });

    if (data.success) {
      // throw a bit of confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: {
          y: 0.6,
        },
      });

      const syncData = await ProviderService.providerSync();
      this.setState({
        syncing: false,
        syncSuccess: syncData.success,
        error: syncData.error,
        missionComplete: syncData.missionComplete,
        counter: syncData.success && !syncData.missionComplete ? 5 : -1,
        confetti: syncData.confetti,
      });

      if (syncData.success) {
        if (syncData.confetti) {
          // throw a bit of confetti
          confetti({
            particleCount: 100,
            spread: 70,
            origin: {
              y: 0.6,
            },
          });
        }
      }
    }

    const interval = setInterval(() => {
      let count = this.state.counter;
      if (count > 0) {
        count--;

        if (count === 0) {
          clearInterval(interval);
          window.location.href = "/mission-control";
        }
      }

      this.setState({
        counter: count,
      });
    }, 750);
  }

  renderConfetti() {
    if (this.state.syncSuccess && this.state.missionComplete)
      return (
        <div>
          <h2>
            Mission accomplished!{" "}
            <span role="img" aria-label="*ta da*">
              🎉
            </span>
          </h2>

          <br />
          <p>
            All done, we have now fetched all the data that was needed for this
            mission.
          </p>
          <p>
            <small>You can close this window.</small>
          </p>
          <TesterOutcomeUpload />
        </div>
      );

    if (this.state.syncSuccess)
      return (
        <div>
          <h2>
            Let&apos;s get started!{" "}
            <span role="img" aria-label="*ta da*">
              🎉
            </span>
          </h2>

          <br />
          <p>We fetched all the data that was needed for this mission.</p>
          <p>
            <small>Starting your mission in {this.state.counter}...</small>
          </p>
        </div>
      );

    if (this.state.authSuccess && this.state.syncing)
      return (
        <div>
          <h2>
            Thanks!
            <span role="img" aria-label="*thumb up*">
              👍
            </span>
          </h2>

          <br />
          <p>You successfully connected to your provider.</p>

          <Alert color="success">
            <Spinner size="sm" /> &nbsp; Please wait while we fetch data that is
            needed for this mission.
          </Alert>
        </div>
      );

    return (
      <div>
        <h2>
          Whoopsie!{" "}
          <span role="img" aria-label="*head explode*">
            🤯
          </span>
        </h2>
        <br />
        <Alert color="danger">
          Something went wrong while communicating with your provider. Please
          try starting over. If the problem persists, please let us know that
          you experienced this error.
        </Alert>

        <p>
          <small>
            Error message was <strong>{this.state.error}</strong>
          </small>
        </p>
        <TesterOutcomeUpload />
      </div>
    );
  }

  renderBoring() {
    if (this.state.syncSuccess && this.state.missionComplete)
      return (
        <div>
          <h2>Completed</h2>

          <br />
          <p>
            All done, we have now fetched all the data that was needed to test
            the connection.
          </p>
          <p>
            <small>You can close this window.</small>
          </p>
          <TesterOutcomeUpload />
          <RequestResponseResults />
        </div>
      );

    if (this.state.syncSuccess)
      return (
        <div>
          <h2>Syncronization successful</h2>

          <br />
          <p>
            We fetched the data that was needed to perform the quality check
          </p>
          <p>
            <small>
              The quality check will begin in {this.state.counter} seconds
            </small>
          </p>
        </div>
      );

    if (this.state.authSuccess && this.state.syncing)
      return (
        <div>
          <h2>Authentication successful</h2>

          <br />
          <p>You successfully connected to your provider.</p>

          <Alert color="success">
            <Spinner size="sm" /> &nbsp; Please wait while we fetch data that is
            needed for the quality check.
          </Alert>
        </div>
      );

    return (
      <div>
        <h2>Something went wrong!</h2>
        <br />
        <Alert color="danger">
          Something went wrong while communicating with your provider. Please
          try starting over. If the problem persists, please let us know that
          you experienced this error.
        </Alert>
        <TesterOutcomeUpload />

        <p>
          <small>
            Error message was <strong>{this.state.error}</strong>
          </small>
        </p>
      </div>
    );
  }

  render() {
    if (this.state.loading) return <p>Loading, please wait...</p>;

    if (this.state.confetti) return this.renderConfetti();

    return this.renderBoring();
  }
}
